import * as Sentry from '@sentry/vue'

import type {
  InfoPage,
  Alert,
  HeroImagePage,
  HeroImageResponse,
} from '@layers/web/services/types/content-api.ts'
import { CACHE_KEYS } from '@layers/web/constants/cache.ts'

export class ContentApiService {
  static async fetchAlert (): Promise<Alert> {
    try {
      const { market } = useRuntimeConfig().public

      return await serverCacheStaleWhileRevalidate(
        CACHE_KEYS.ALERT,
        async () => {
          const { data: res } = await apiFetch<Alert>(
            `/${market}/alert`,
            { retryDelay: 5000 }
          )
          return res
        },
        { retryDelayMs: 5000 }
      )
    } catch (error: any) {
      Sentry.setContext('details', error.data)
      Sentry.captureException(new Error('Failed to fetch alert'), {
        level: 'error',
        tags: {
          operation: 'fetchAlert',
          service: 'content-api',
        },
      })

      throw error
    }
  }

  static async fetchHeroImage (page: HeroImagePage): Promise<HeroImageResponse> {
    const { market } = useRuntimeConfig().public

    try {
      const { data: res } = await apiFetch<HeroImageResponse>(`/${market}/heroimage?page=${page}`, {
        throwOnFail: false,
      })

      return res
    } catch (error: any) {
      Sentry.setContext('details', error.data)
      Sentry.captureException(new Error('Failed to fetch hero image'), {
        level: 'error',
        tags: {
          operation: 'fetchHeroImage',
          service: 'content-api',
        },
      })

      throw error
    }
  }

  static async listAllInfoPages (): Promise<InfoPage[]> {
    try {
      const { market } = useRuntimeConfig().public
      const { data: result } = await apiFetch<InfoPage[]>(`/${market}/info-pages`)
      return result
    } catch (error: any) {
      Sentry.setContext('details', error.data)
      Sentry.captureException(new Error('Failed to fetch info pages'), {
        level: 'error',
        tags: {
          operation: 'listAllInfoPages',
          service: 'content-api',
        },
      })

      throw error
    }
  }
}
